import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { searchTranscriptions } from "../api";
import type { SearchHit } from "../types";
import { fmtDuration, fmtRelative } from "../format";

const SOH = String.fromCharCode(1);
const STX = String.fromCharCode(2);

function Snippet({ raw }: { raw: string }) {
  const parts: Array<{ text: string; mark: boolean }> = [];
  let buf = "";
  let mark = false;
  for (const ch of raw) {
    if (ch === SOH) { if (buf) parts.push({ text: buf, mark }); buf = ""; mark = true; }
    else if (ch === STX) { if (buf) parts.push({ text: buf, mark }); buf = ""; mark = false; }
    else buf += ch;
  }
  if (buf) parts.push({ text: buf, mark });
  return (
    <>
      {parts.map((p, i) => p.mark ? <mark key={i}>{p.text}</mark> : <span key={i}>{p.text}</span>)}
    </>
  );
}

export function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const q = searchParams.get("q")?.trim() ?? "";
  const [input, setInput] = useState(q);
  const [hits, setHits] = useState<SearchHit[] | null>(null);

  useEffect(() => { setInput(q); }, [q]);

  useEffect(() => {
    if (!q) { setHits([]); return; }
    let cancelled = false;
    setHits(null);
    searchTranscriptions(q).then((r) => { if (!cancelled) setHits(r); }).catch(() => { if (!cancelled) setHits([]); });
    return () => { cancelled = true; };
  }, [q]);

  return (
    <>
      <header className="masthead">
        <h1 className="masthead-title"><Link to="/" className="bare">Transcripts</Link></h1>
        <span></span>
        <Link className="backlink bare" to="/">← Index</Link>
      </header>

      <form
        className="search-bar"
        role="search"
        onSubmit={(e) => { e.preventDefault(); setSearchParams(input ? { q: input } : {}); }}
      >
        <input
          type="search"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="A word, a phrase, a name…"
          autoFocus
          aria-label="Search query"
        />
      </form>

      {!q ? (
        <div className="empty">Type a query above to search across all transcripts.</div>
      ) : hits == null ? (
        <div className="empty">Searching…</div>
      ) : hits.length === 0 ? (
        <div className="empty">No matches for <em>{q}</em>.</div>
      ) : (
        <div className="results">
          {hits.map((h) => {
            const name = h.title?.trim() || h.filename;
            return (
              <Link
                key={h.id}
                className="result"
                to={`/transcriptions/${h.id}?q=${encodeURIComponent(q)}`}
              >
                <div className="result-head">
                  <span className="result-name">{name}</span>
                  <span className="result-meta">{fmtDuration(h.duration_secs)} · {fmtRelative(h.created_at)}</span>
                </div>
                <div className="result-snippet"><Snippet raw={h.snippet ?? ""} /></div>
              </Link>
            );
          })}
        </div>
      )}
    </>
  );
}
